import { get_token } from "@/router";
import { build_message_blocks } from "./bolide_assistant_message_blocks";

const backend_base_url = process.env.VUE_APP_BACKEND_BASE_URL;
const assist_query_url = process.env.VUE_APP_ASSIST_QUERY_URL;

export default {
  name: "BolideAssistantMessages",

  props: {
    messages: {
      type: Array,
      default: () => [],
    },
    has_older_messages: Boolean,
    is_loading_older_messages: Boolean,
  },

  emits: ["load-older-messages"],

  watch: {
    messages: {
      deep: true,
      handler() {
        if (this.is_loading_older_messages) return;
        this.scroll_to_bottom({ settle: true });
      },
    },
  },

  mounted() {
    this.scroll_to_bottom({ settle: true });
  },

  methods: {
    handle_scroll(event) {
      if (!this.has_older_messages || this.is_loading_older_messages) return;
      if (event.currentTarget.scrollTop < 80) this.$emit("load-older-messages");
    },

    handle_message_click(event) {
      const download_link = event.target.closest(".chatbot-message-document");
      if (!download_link) return;

      event.preventDefault();
      this.download_report_pdf(download_link.dataset.downloadHref || download_link.href)
        .catch((error) => {
          console.error("PDF download failed.", error);
        });
    },

    capture_scroll_state() {
      const element = this.$refs.messages_body;
      if (!element) return null;
      return ({
        scroll_height: element.scrollHeight,
        scroll_top: element.scrollTop,
      });
    },

    restore_scroll_state(scroll_state) {
      if (!scroll_state) return;
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const element = this.$refs.messages_body;
          if (!element) return;
          element.scrollTop = element.scrollHeight
            - scroll_state.scroll_height
            + scroll_state.scroll_top;
        });
      });
    },

    get_thinking_status(message) {
      const steps = Array.isArray(message?.thinking_steps) ? message.thinking_steps : [];

      if (steps.some((step) => step.status === "failed")) {
        return "failed";
      }

      if (steps.length && steps.every((step) => step.status === "complete")) {
        return "complete";
      }

      return "active";
    },

    get_thinking_status_label(message) {
      const status = this.get_thinking_status(message);

      if (status === "failed") {
        return "Needs attention";
      }

      if (status === "complete") {
        return "Ready";
      }

      const steps = Array.isArray(message?.thinking_steps) ? message.thinking_steps : [];
      const completed_count = steps.filter((step) => step.status === "complete").length;

      return steps.length ? `${completed_count}/${steps.length}` : "Starting";
    },

    get_message_created_at(message) {
      if (message?.created_at) {
        return message.created_at;
      }

      const timestamp_from_id = String(message?.id || "").split("-")[0];
      const timestamp = Number(timestamp_from_id);

      return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : "";
    },

    format_message_timestamp(message) {
      if (message?.is_typing) {
        return "";
      }

      const created_at = this.get_message_created_at(message);
      if (!created_at) {
        return "";
      }

      const date = new Date(created_at);
      if (Number.isNaN(date.getTime())) {
        return "";
      }

      return new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
    },

    get_readable_message_text(message) {
      const text = String(message?.text || "").trim();
      if (!text || message?.sender !== "assistant") {
        return text;
      }

      if (text.length < 180 || /\n\s*\n/.test(text)) {
        return text;
      }

      let formatted_text = text
        .replace(
          /\s+(?=(Investigate|Review|Check|Validate|Compare|Confirm|Prioritize|Do not|Treat|Use|Flag|Escalate)\b)/g,
          "\n\n"
        )
        .replace(/:\s+(?=\d+\s)/g, ":\n")
        .replace(/\s+(?=\d+\s+(suspected|empty|unusually|daily|hourly)\b)/gi, "\n");

      if (formatted_text === text) {
        formatted_text = text.replace(/([.!?])\s+(?=[A-Z][a-z])/g, "$1\n\n");
      }

      return formatted_text.trim();
    },

    get_message_blocks(message) {
      const text = this.get_readable_message_text(message);
      return build_message_blocks(
        text,
        message?.sender === "assistant",
        (url) => this.get_absolute_download_href(url)
      );
    },

    async download_report_pdf(url) {
      const auth_token = get_token();
      if (!url || !auth_token) return;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Token ${auth_token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`PDF download failed with status ${response.status}`);
      }

      const content_type = response.headers.get("content-type") || "";
      if (!content_type.toLowerCase().includes("application/pdf")) {
        throw new Error("PDF download returned a non-PDF response.");
      }

      const blob = await response.blob();
      const blob_url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blob_url;
      link.download = this.get_download_filename(response) || "bolide-assist-report.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blob_url);
    },

    get_download_filename(response) {
      const disposition = response.headers.get("content-disposition") || "";
      const utf8_match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
      if (utf8_match) return decodeURIComponent(utf8_match[1].replace(/"/g, ""));

      const filename_match = disposition.match(/filename="?([^";]+)"?/i);
      return filename_match ? filename_match[1] : "";
    },

    get_absolute_download_href(url) {
      const clean_url = String(url || "").replaceAll("\\", "").trim();
      if (!this.is_pdf_download_url(clean_url)) return "";

      try {
        const download_base_url = this.get_download_base_url();
        const parsed_url = download_base_url
          ? new URL(clean_url, download_base_url)
          : new URL(clean_url);
        const report_download_path = this.get_report_download_path(parsed_url.pathname);
        if (!report_download_path) return "";
        return download_base_url
          ? new URL(report_download_path, download_base_url).toString()
          : parsed_url.toString();
      } catch {
        return "";
      }
    },

    get_report_download_path(pathname) {
      const path_parts = String(pathname || "").split("/").filter(Boolean);
      const reports_index = path_parts.lastIndexOf("reports");
      const report_id = reports_index === -1 ? "" : path_parts[reports_index + 1];
      const suffix = path_parts.slice(reports_index + 2).join("/");

      if (!report_id || Number.isNaN(Number(report_id)) || suffix !== "pdf/download") {
        return "";
      }

      return `/aic_auth_api/bolide_assist_api/reports/${report_id}/pdf/download/`;
    },

    is_pdf_download_url(url) {
      const clean_url = String(url || "").trim();
      const path_without_hash = clean_url.split("#")[0];
      const path_without_query = path_without_hash.split("?")[0];
      return path_without_query.endsWith("/pdf/download/")
        || path_without_query.endsWith("/pdf/download");
    },

    get_download_base_url() {
      const assist_url = String(assist_query_url || "").trim();
      if (assist_url) {
        try {
          return new URL(assist_url, window.location.origin).origin;
        } catch {
          return "";
        }
      }

      const backend_url = String(backend_base_url || "").trim();
      try {
        return new URL(backend_url).origin;
      } catch {
        return "";
      }
    },

    scroll_messages_body_to_bottom() {
      const el = this.$refs.messages_body;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    },

    scroll_to_bottom({ settle = false } = {}) {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.scroll_messages_body_to_bottom();
          requestAnimationFrame(() => {
            this.scroll_messages_body_to_bottom();
          });
        });

        if (settle) {
          setTimeout(() => {
            this.scroll_messages_body_to_bottom();
          }, 80);
          setTimeout(() => {
            this.scroll_messages_body_to_bottom();
          }, 220);
        }
      });
    },
  },
};
